/**
 * Integrytas Group - Common Security Module (CSM)
 * Root application component. Routes between:
 *   first_run        -> FirstAdminSetupScreen (only when csm_user_master is empty)
 *   sign_in          -> SignInScreen
 *   register         -> RegisterScreen
 *   forgot_password  -> ForgotPasswordScreen
 *   authenticated    -> TabManagerShell + page registry
 */

import { useEffect, useMemo, useState } from 'react';
import { useAuthSession } from './lib/use_auth_session';
import { useActiveLocale } from './lib/use_active_locale';
import { setTelemetrySession, reportError } from './lib/telemetry';
import { reportNavigationTiming } from './lib/web_vitals';
import { resolveIamLogin } from './lib/iam';
import { markAsfSessionStart } from './lib/asf';
import { usePageSync } from './lib/use_page_sync';
import { useUdcHostSync } from './lib/udc_hosts';
import { useFlnFieldSync } from './lib/fln_fields';
import { loadPageLabels } from './lib/auth_loader';
import { setStoragePrincipal, scopedKey, purgeLegacyShellState } from './lib/storage_principal';
import { SignInScreen } from './components/auth/SignInScreen';
import { RegisterScreen } from './components/auth/RegisterScreen';
import { ForgotPasswordScreen } from './components/auth/ForgotPasswordScreen';
import { SetNewPasswordScreen } from './components/auth/SetNewPasswordScreen';
import { FirstAdminSetupScreen } from './components/auth/FirstAdminSetupScreen';
import { ChangePasswordModal } from './components/auth/ChangePasswordModal';
import { Modal } from './components/common/Modal';
import { TabManagerShell, type OpenTab } from './components/layout/TabManagerShell';
import { load_menu_entitlement, filter_menu_by_entitlement, type menu_entitlement } from './lib/license_menu';
import { PAGE_COMPONENTS } from './pages/page_components';
import { ApprovalPage } from './pages/approval/ApprovalPage';
import { PortalLoginPage } from './pages/portal/PortalLoginPage';
import { Watermark } from './components/common/Watermark';
import { APPLICATION_NAME, BRANDING_SUFFIX } from './constants/branding';
import { setActiveCsmLocale, loadI18nOverrides } from './i18n';

type AuthView = 'sign_in' | 'register' | 'forgot_password';

export default function App() {
  // Public approval magic link short-circuits the auth flow.
  // Path: /approve?token=<uuid>[&action=approve_all|reject_all]
  if (typeof window !== 'undefined' && window.location.pathname === '/approve') {
    const params = new URLSearchParams(window.location.search);
    const token  = params.get('token');
    const action = params.get('action');
    if (token) {
      return <ApprovalPage token={token} action={action} />;
    }
  }

  // Third-party partner portal — two-phase login (separate from the app login).
  // Path: /portal
  if (typeof window !== 'undefined' && window.location.pathname === '/portal') {
    return <PortalLoginPage />;
  }

  const auth = useAuthSession();
  const activeLocale = useActiveLocale();
  const sync = usePageSync(auth.session);
  useUdcHostSync(auth.session);   // register UDC host (page, entity) candidates
  useFlnFieldSync(auth.session);  // sync discovered field-lineage (page, field) set

  // Drop the pre-scoping tab keys once, so the workspace they hold — whoever
  // left it there — cannot be adopted by the next user to sign in.
  useEffect(() => { purgeLegacyShellState(); }, []);

  // Keep telemetry stamped with the current session.
  useEffect(() => {
    setTelemetrySession(auth.session);
  }, [auth.session]);

  // Report client navigation-timing once a session exists (PDE web-vitals tier).
  useEffect(() => {
    if (auth.session) reportNavigationTiming();
  }, [auth.session]);

  // Federated (SSO) login: JIT-provision + apply role mappings. No-op for password.
  useEffect(() => {
    if (auth.session) void resolveIamLogin();
  }, [auth.session]);

  // ASF: mark the session start time for fingerprint duration tokens.
  useEffect(() => {
    if (auth.session) markAsfSessionStart();
  }, [auth.session]);

  // Global unhandled error/rejection handlers.
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      void reportError(e.error ?? new Error(e.message), { errorType: 'UNHANDLED', severity: 'FATAL' });
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      void reportError(e.reason ?? new Error('Unhandled promise rejection'), {
        errorType: 'UNHANDLED', severity: 'ERROR',
      });
    };
    window.addEventListener('error',               onError);
    window.addEventListener('unhandledrejection',  onRejection);
    return () => {
      window.removeEventListener('error',              onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);
  const [pageLabels, setPageLabels]         = useState<Record<string, string>>({});
  const [menuEnt, setMenuEnt]               = useState<menu_entitlement | null>(null);
  const [authView, setAuthView]             = useState<AuthView>('sign_in');
  const [isFirstRun, setFirstRun]           = useState<boolean | null>(null);
  const [changePasswordOpen, setCpOpen]     = useState(false);
  // Mimic (impersonation) reason capture — a proper in-app modal, not window.prompt.
  const [mimicTarget, setMimicTarget]       = useState<number | null>(null);
  const [mimicReason, setMimicReason]       = useState('');
  const [mimicError, setMimicError]         = useState<string | null>(null);
  const [mimicBusy, setMimicBusy]           = useState(false);

  // First-run probe.
  useEffect(() => {
    if (auth.status !== 'unauthenticated') return;
    let cancelled = false;
    void auth.checkFirstRun().then(v => { if (!cancelled) setFirstRun(v); });
    return () => { cancelled = true; };
  }, [auth.status, auth]);

  // Reload localized page labels after each sync (new pages may have appeared) and
  // whenever the ACTIVE locale changes (§8) — so menu/tab/breadcrumb titles follow a
  // runtime language switch, not the stale stored preferred_locale.
  useEffect(() => {
    if (auth.status !== 'authenticated' || !auth.session) return;
    const ids = auth.session.effective_user.page_access.map(p => p.page_id);
    void loadPageLabels(ids, activeLocale)
      .then(setPageLabels)
      .catch(() => setPageLabels({}));
  }, [auth.status, auth.session, sync.ran, sync.inserted, sync.updated, activeLocale]);

  // Resolve which licensable modules may appear in the menu (verified licence,
  // else the local allow-list parameter). Drives the navigation gating below.
  useEffect(() => {
    if (auth.status !== 'authenticated' || !auth.session) { setMenuEnt(null); return; }
    let cancelled = false;
    void load_menu_entitlement()
      .then(e => { if (!cancelled) setMenuEnt(e); })
      .catch(() => { if (!cancelled) setMenuEnt(null); });
    return () => { cancelled = true; };
  }, [auth.status, auth.session]);

  // Drive the i18next active language from the authenticated user's
  // preferred_locale. Unauthenticated screens stay in the default (English).
  useEffect(() => {
    const locale =
      auth.status === 'authenticated' && auth.session
        ? auth.session.effective_user.preferred_locale
        : 'EN_US';
    setActiveCsmLocale(locale);
    // Layer DB prompt-overrides for this locale over the static defaults.
    if (auth.status === 'authenticated') void loadI18nOverrides(locale);
  }, [auth.status, auth.session]);

  // The menu the shell sees, gated by module entitlement.
  const licensedMenu = useMemo(() => {
    const pa = auth.session?.effective_user.page_access ?? [];
    return menuEnt ? filter_menu_by_entitlement(pa, menuEnt) : pa;
  }, [auth.session, menuEnt]);

  // Point browser-persisted shell state at the EFFECTIVE user before anything
  // reads it. Effective, not real, so a mimic session opens the mimicked user's
  // workspace — the same identity their permissions and menu already follow
  // (§12). Set during render because the reads below, and the shell's own lazy
  // state initialisers, run before any effect could fire.
  const storagePrincipal = auth.session?.effective_user.user_id ?? null;
  setStoragePrincipal(storagePrincipal);

  const initialTabs = useMemo<OpenTab[]>(() => {
    try {
      const raw = sessionStorage.getItem(scopedKey('csm.openTabs', storagePrincipal));
      return raw ? (JSON.parse(raw) as OpenTab[]) : [];
    } catch { return []; }
  }, [storagePrincipal]);

  // ---------------------------------------------------------------------------
  // Password recovery short-circuit. Supabase has consumed the recovery
  // token from the email link and emitted PASSWORD_RECOVERY; the user is
  // technically signed in but must set a new password before proceeding.
  // This branch must sit ABOVE the regular authenticated branch so the
  // user can never bypass the prompt by waiting for the shell to load.
  // ---------------------------------------------------------------------------
  if (auth.isPasswordRecovery) {
    return (
      <SetNewPasswordScreen
        onSubmit={auth.completePasswordRecovery}
        onCancel={auth.signOut}
        errorMessage={auth.error}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Loading splash
  // ---------------------------------------------------------------------------
  if (auth.status === 'loading') {
    return (
      <div className="relative flex h-screen w-screen items-center justify-center bg-white text-csm-md text-csm-muted">
        <Watermark appName={APPLICATION_NAME} brandingSuffix={BRANDING_SUFFIX} />
        Loading session...
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Unauthenticated
  // ---------------------------------------------------------------------------
  if (auth.status !== 'authenticated' || !auth.session) {
    if (isFirstRun) {
      return <FirstAdminSetupScreen onCreateAdmin={auth.signUp} errorMessage={auth.error} />;
    }
    if (authView === 'register') {
      return (
        <RegisterScreen
          onRegister={auth.signUp}
          onBackToSignIn={() => setAuthView('sign_in')}
          errorMessage={auth.error}
        />
      );
    }
    if (authView === 'forgot_password') {
      return (
        <ForgotPasswordScreen
          onRequestReset={auth.requestPasswordReset}
          onBackToSignIn={() => setAuthView('sign_in')}
          errorMessage={auth.error}
        />
      );
    }
    return (
      <SignInScreen
        onSignIn={auth.signIn}
        onDevLogin={auth.devLogin}
        onRequestRegister={() => setAuthView('register')}
        onRequestForgot={() => setAuthView('forgot_password')}
        errorMessage={auth.error}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Authenticated - render via PAGE_COMPONENTS registry
  // ---------------------------------------------------------------------------
  const session = auth.session;
  // Open the mimic-reason modal (the native window.prompt it replaced couldn't be
  // styled, broke the §11/§17 modal standard, and blocked automated/embedded use).
  const handleBeginMimic = (userId: number) => {
    setMimicReason('');
    setMimicError(null);
    setMimicTarget(userId);
  };
  const confirmMimic = async () => {
    if (mimicTarget == null) return;
    if (mimicReason.trim().length < 5) {
      setMimicError('A business reason of at least 5 characters is required.');
      return;
    }
    setMimicBusy(true); setMimicError(null);
    try {
      await auth.beginMimic(mimicTarget, mimicReason.trim());
      setMimicTarget(null);
    } catch (e) {
      setMimicError((e as Error).message);
    } finally {
      setMimicBusy(false);
    }
  };

  const renderPage = (tab: OpenTab) => {
    const Component = PAGE_COMPONENTS[tab.page_internal_code];
    if (Component) {
      return <Component session={session} onBeginMimic={handleBeginMimic} />;
    }
    return (
      <div className="p-6">
        <div className="mb-2 text-csm-sm uppercase tracking-wider text-csm-muted">
          {tab.page_internal_code}
        </div>
        <div className="rounded border border-csm-border bg-white p-4 text-csm-md text-csm-ink-soft">
          No component registered for <code>{tab.page_internal_code}</code>.
          Add it to <code>src/pages/page_components.tsx</code>.
        </div>
      </div>
    );
  };

  return (
    <>
      <TabManagerShell
        // Remount on a change of principal — sign-in as someone else, or a
        // mimic starting or ending — so the shell's lazy state re-hydrates
        // from that user's keys instead of keeping the previous workspace.
        key={storagePrincipal ?? 'anon'}
        session={auth.session}
        menu={licensedMenu}
        pageLabels={pageLabels}
        initialTabs={initialTabs}
        onSignOut={() => { void auth.signOut(); }}
        onEndMimic={() => { void auth.endMimic(); }}
        onChangePassword={() => setCpOpen(true)}
        renderPage={renderPage}
      />
      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setCpOpen(false)}
        onSubmit={auth.changePassword}
      />

      {mimicTarget != null && (
        <Modal
          title="Begin mimic session"
          width="md"
          onClose={() => { if (!mimicBusy) setMimicTarget(null); }}
          footer={
            <>
              <button onClick={() => setMimicTarget(null)} disabled={mimicBusy}
                className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft disabled:opacity-60">Cancel</button>
              <button onClick={() => void confirmMimic()} disabled={mimicBusy}
                className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
                {mimicBusy ? 'Starting…' : 'Begin mimic'}
              </button>
            </>
          }
        >
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">Business reason</label>
          <textarea autoFocus value={mimicReason} onChange={e => setMimicReason(e.target.value)} rows={3}
            placeholder="Why are you impersonating this user? (recorded against the real admin)"
            className="w-full rounded border border-csm-border px-2 py-1 text-csm-sm focus:border-primary focus:outline-none" />
          <p className="mt-1 text-csm-2xs text-csm-watermark">All actions during the session are stamped with your real admin identity.</p>
          {mimicError && (
            <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{mimicError}</div>
          )}
        </Modal>
      )}
    </>
  );
}
